// Clean up the HTML that comes out of RichBodyEditor before it goes to
// the recipient (and into ticket_messages.body_html for the in-app
// thread display).
//
// Why this exists: contentEditable's default Enter behaviour in Chrome
// wraps each line in a <div> and inserts <div><br></div> empty rows for
// blank lines. That empty <br>-only div renders as a FULL line of
// vertical space (~26px at our line-height) — so a user who hit Enter
// twice between paragraphs ends up with ~50px gaps in Gmail / Outlook /
// our own thread bubble. The reply looks unprofessionally airy and the
// client wonders why the spacing is so weird.
//
// The transform:
//   1. Strip empty <div><br></div> (and <div></div>) spacers entirely.
//      Paragraph margins on the surrounding blocks provide the visual
//      gap already; we don't need empty rows on top.
//   2. Convert plain content <div>...</div> to <p>...</p> so every
//      modern mail client + our MessageBody [&_p]:my-2 rule give them
//      consistent, tight margins.
//   3. Collapse runs of consecutive <br>s to a single <br>. Catches
//      pasted-text shapes and older composer states.
//
// What it deliberately does NOT touch:
//   - Inbound emails. Client formatting (signatures, table layouts,
//     intentional spacing) varies wildly; normalising it risks breaking
//     a legit layout. We only run this on OUR own outbound composition.
//   - Nested or structured divs (containing <p>, lists, blockquotes,
//     etc.). The regex's lookahead skips those so we don't double-wrap.
//   - <p> tags that were already there. Idempotent — safe to run twice.

// A <div> whose content is flat inline markup (no block-level child), so it can
// be rewritten to a <p> without double-wrapping or breaking nested structure.
const FLAT_DIV_RE =
  /<div[^>]*>((?:(?!<\/?(?:div|p|ul|ol|blockquote|pre|h[1-6]|table)\b)[\s\S])*?)<\/div>/gi;

/** Normalise contentEditable-style HTML for an OUTBOUND email body.
 *  Idempotent. Safe to apply at both send time AND render time.
 *
 *  This is the RENDER-time pass: it strips blank rows and relies on `<p>`
 *  margins for the gaps, which is how every message sent before
 *  `prepareOutboundHtml` existed was stored. Don't change that — it would
 *  tighten every historical reply. New sends use `prepareOutboundHtml`. */
export function normalizeOutboundHtml(html: string): string {
  if (!html) return '';
  let out = html;

  // 1. Drop empty <div><br></div> spacers. The `<br>+` covers cases
  //    where the editor stacked multiple <br>s in a single empty div.
  //    Whitespace tolerance for hand-edited or pasted markup.
  out = out.replace(/<div[^>]*>\s*(?:<br\s*\/?>\s*)+<\/div>/gi, '');
  //    Drop fully empty divs too.
  out = out.replace(/<div[^>]*>\s*<\/div>/gi, '');

  // 2. Convert FLAT content <div>...</div> to <p>...</p>. The negative
  //    lookahead skips any <div> that contains a block-level child
  //    (<div>, <p>, <ul>, <ol>, <blockquote>, <pre>, <h1>–<h6>, <table>)
  //    so we don't double-wrap or break nested structures. The
  //    `[\s\S]*?` non-greedy match handles inline content (text, <br>,
  //    <strong>, <em>, <a>, <img>, etc.) without crossing into the
  //    next sibling.
  out = out.replace(FLAT_DIV_RE, '<p>$1</p>');

  // 3. Collapse 2+ consecutive <br>s to single. Each <br> is one line
  //    of vertical space — a row of them is the OTHER way users
  //    accidentally get giant gaps (Shift+Enter held down).
  out = out.replace(/(?:<br\s*\/?>\s*){2,}/gi, '<br>');

  // 4. Trim leading <br>s right after the opening of the body — the
  //    editor sometimes leaves one when the user deleted the first
  //    line. No-op when not present.
  out = out.replace(/^(?:\s*<br\s*\/?>\s*)+/i, '');

  return out;
}

// One blank row as the editor can leave it: `<p><br></p>` (any attributes) or a
// bare `<br>`. Used to trim the start and end of the body.
const BLANK_ROW = String.raw`(?:\s*<p\b[^>]*>\s*(?:<br\s*\/?>\s*)+<\/p>|\s*<br\s*\/?>)`;
const LEADING_BLANK_ROWS_RE = new RegExp(`^${BLANK_ROW}+\\s*`, 'i');
const TRAILING_BLANK_ROWS_RE = new RegExp(`${BLANK_ROW}+\\s*$`, 'i');

/** Prepare the composer's HTML for SENDING, so the recipient sees the spacing
 *  the agent saw.
 *
 *  The composer renders every `<p>` with zero margin (Tailwind preflight), so
 *  there a blank line is exactly one blank row. Mail clients and our own thread
 *  view give `<p>` a margin on top (~1em in Gmail, `my-2` in MessageBody), which
 *  turns the same markup into roughly two blank lines. Rather than strip the
 *  blank rows and hope the margins land right, this carries the composer's
 *  model with the message: every paragraph is `margin:0`, blank rows stay as
 *  blank rows, and nothing the client adds can double the gap.
 *
 *    1. Blank `<div><br></div>` rows become `<p><br></p>` (kept, not stripped).
 *    2. Flat `<div>` rows become `<p>`.
 *    3. Leading / trailing blank rows are trimmed.
 *    4. Every `<p>` gets `margin:0` (merged into an existing `style`).
 *
 *  Deliberately NOT done: collapsing runs of blank rows or `<br>`s. Two blank
 *  lines typed is two blank lines sent.
 *
 *  Idempotent. Send-time only — rendering old rows uses normalizeOutboundHtml. */
export function prepareOutboundHtml(html: string): string {
  if (!html) return '';
  let out = html;

  out = out.replace(/<div[^>]*>\s*(?:<br\s*\/?>\s*)*<\/div>/gi, '<p><br></p>');
  out = out.replace(FLAT_DIV_RE, '<p>$1</p>');

  out = out.replace(LEADING_BLANK_ROWS_RE, '').replace(TRAILING_BLANK_ROWS_RE, '');

  out = out.replace(/<p\b([^>]*)>/gi, (whole, attrs: string) => {
    const style = attrs.match(/\sstyle\s*=\s*(["'])([\s\S]*?)\1/i);
    if (!style) return `<p${attrs} style="margin:0">`;
    const [raw, quote, css] = style;
    if (/(^|;)\s*margin\s*:/i.test(css)) return whole;
    return `<p${attrs.replace(raw, () => ` style=${quote}margin:0;${css}${quote}`)}>`;
  });

  return out;
}

/** Plain-text projection of the composer's HTML: <br> and block ends become
 *  newlines, tags are dropped, common entities decoded. Used for the text/plain
 *  MIME part + `ticket_messages.body`, for "is the composer empty?" checks and
 *  for AI prompts. Shared by ReplyComposer, ComposeDialog and the send pipeline
 *  (lib/outbound-send.ts) — it used to be copy-pasted into each. */
export function htmlToPlain(html: string): string {
  if (!html) return '';
  return html
    .replace(/<\s*br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|h[1-6]|tr)>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** Sanitise HTML pasted from Slack / Gmail / another editor down to a SAFE
 *  subset before it enters the contentEditable. Keeps links + basic formatting
 *  (bold / italic / underline / strike / lists / paragraphs / blockquote /
 *  headings) and DROPS everything else — inline styles, colours, fonts,
 *  classes, scripts, tables, etc. So pasted content doesn't drag messy web
 *  styling into the email body, while preserving the thing people actually
 *  care about: the hyperlinks (which the old "force plain text" paste path
 *  silently destroyed). Disallowed tags are UNWRAPPED (their text + nested
 *  links survive). Only http(s)/mailto hrefs are kept — never javascript: etc. */
export function sanitizePastedHtml(rawHtml: string): string {
  if (!rawHtml) return '';
  const ALLOWED = new Set([
    'A', 'B', 'STRONG', 'I', 'EM', 'U', 'S', 'P', 'BR',
    'UL', 'OL', 'LI', 'BLOCKQUOTE', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
  ]);
  let doc: Document;
  try {
    doc = new DOMParser().parseFromString(rawHtml, 'text/html');
  } catch {
    return '';
  }

  function transfer(src: Node, dest: Node): void {
    src.childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        dest.appendChild(document.createTextNode(child.textContent ?? ''));
        return;
      }
      if (child.nodeType !== Node.ELEMENT_NODE) return; // drop comments etc.
      const el = child as HTMLElement;
      const tag = el.tagName;
      if (!ALLOWED.has(tag)) {
        // Unwrap: keep cleaned children so text + links inside a <span style>,
        // <font>, <div>, etc. still come through.
        transfer(el, dest);
        return;
      }
      const clone = document.createElement(tag.toLowerCase());
      if (tag === 'A') {
        const href = (el.getAttribute('href') || '').trim();
        if (/^(https?:|mailto:)/i.test(href)) {
          clone.setAttribute('href', href);
          clone.setAttribute('target', '_blank');
          clone.setAttribute('rel', 'noopener noreferrer');
        }
      }
      transfer(el, clone);
      dest.appendChild(clone);
    });
  }

  const root = document.createElement('div');
  transfer(doc.body, root);
  return root.innerHTML;
}
